/*
 * /assets/store-locator.js
 * Client-side filtering for the Find a Store section. Reads:
 *   - <script type="application/json" data-store-data>   full store list
 *   - <input data-store-search>                          search input
 *   - <div  data-state-filter>                           container for state chips
 *   - <article data-store data-state data-search>...     card elements
 *   - <section data-state-group>                          group wrappers
 *   - <p data-result-count data-count-template-one data-count-template-other>  count line
 *   - <div  data-empty>                                  empty-state container
 *
 * i18n: the count line is rebuilt from localised templates carried on the
 *   [data-result-count] element (data-count-template-one / -other), each holding
 *   the translated string with a literal {{ count }} token. This lets every
 *   locale control word order (e.g. DE "12 Geschaefte angezeigt"). Chip labels come
 *   from state_name in the JSON data, which is already localised server-side.
 *
 * URL params:
 *   ?q=<term>      Pre-fills the search box and applies the filter on load.
 *   ?state=<code>  Activates a specific state chip on load (e.g. ?state=DE).
 */

(function () {
  'use strict';

  function init(section) {
    if (!section || section.dataset.storeLocatorReady === '1') return;
    section.dataset.storeLocatorReady = '1';

    var dataEl = section.querySelector('[data-store-data]');
    var searchInput = section.querySelector('[data-store-search]');
    var chipContainer = section.querySelector('[data-state-filter]');
    var resultsContainer = section.querySelector('[data-results]');
    var countLine = section.querySelector('[data-result-count]');
    var emptyEl = section.querySelector('[data-empty]');

    if (!dataEl || !searchInput || !chipContainer || !resultsContainer) return;

    // Localised count templates (plain strings holding a literal {{ count }} token).
    // Server render uses CLDR pluralization; client re-render swaps the token here.
    var tplOne = (countLine && countLine.getAttribute('data-count-template-one')) || 'Showing {{ count }} store';
    var tplOther = (countLine && countLine.getAttribute('data-count-template-other')) || 'Showing {{ count }} stores';

    function renderCount(n) {
      if (!countLine) return;
      var tpl = (n === 1) ? tplOne : tplOther;
      countLine.textContent = tpl.replace('{{ count }}', String(n)).replace('{{count}}', String(n));
    }

    var stores = [];
    try {
      stores = JSON.parse(dataEl.textContent || '[]');
    } catch (err) {
      console.warn('[store-locator] Failed to parse store data:', err);
      stores = [];
    }

    var cards = Array.prototype.slice.call(resultsContainer.querySelectorAll('[data-store]'));
    var groups = Array.prototype.slice.call(resultsContainer.querySelectorAll('[data-state-group]'));

    var stateMap = {};
    stores.forEach(function (s) {
      if (s.state && !stateMap[s.state]) {
        stateMap[s.state] = s.state_name || s.state;
      }
    });

    var stateCodes = Object.keys(stateMap).sort(function (a, b) {
      return stateMap[a].localeCompare(stateMap[b]);
    });

    stateCodes.forEach(function (code) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'store-locator__chip';
      btn.setAttribute('data-state-chip', code);
      btn.textContent = stateMap[code];
      chipContainer.appendChild(btn);
    });

    var activeState = 'all';
    var activeQuery = '';

    function normalize(s) {
      return (s || '').toLowerCase().trim();
    }

    function applyFilter() {
      var query = normalize(activeQuery);
      var visibleCount = 0;
      var groupVisibility = {};

      cards.forEach(function (card) {
        var cardState = card.getAttribute('data-state') || '';
        var haystack = card.getAttribute('data-search') || '';

        var matchesState = activeState === 'all' || cardState === activeState;
        var matchesQuery = query === '' || haystack.indexOf(query) !== -1;
        var visible = matchesState && matchesQuery;

        if (visible) {
          card.classList.remove('is-hidden');
          visibleCount++;
          groupVisibility[cardState] = true;
        } else {
          card.classList.add('is-hidden');
        }
      });

      groups.forEach(function (group) {
        var code = group.getAttribute('data-state-group') || '';
        if (groupVisibility[code]) {
          group.classList.remove('is-hidden');
        } else {
          group.classList.add('is-hidden');
        }
      });

      renderCount(visibleCount);

      if (emptyEl) {
        if (visibleCount === 0 && stores.length > 0) {
          emptyEl.hidden = false;
          resultsContainer.setAttribute('hidden', '');
        } else {
          emptyEl.hidden = true;
          resultsContainer.removeAttribute('hidden');
        }
      }
    }

    var searchDebounce;
    searchInput.addEventListener('input', function () {
      activeQuery = searchInput.value;
      clearTimeout(searchDebounce);
      searchDebounce = setTimeout(applyFilter, 80);
    });

    chipContainer.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.matches || !target.matches('[data-state-chip]')) return;
      var code = target.getAttribute('data-state-chip');
      if (!code) return;

      activeState = code;
      Array.prototype.forEach.call(chipContainer.querySelectorAll('[data-state-chip]'), function (chip) {
        chip.classList.toggle('is-active', chip === target);
      });
      applyFilter();
    });

    function readUrlParams() {
      try {
        var params = new URLSearchParams(window.location.search);
        var q = params.get('q');
        var stateParam = params.get('state');

        if (q) {
          searchInput.value = q;
          activeQuery = q;
        }

        if (stateParam) {
          var code = stateParam.toUpperCase();
          if (stateMap[code]) {
            activeState = code;
            var chip = chipContainer.querySelector('[data-state-chip="' + code + '"]');
            if (chip) {
              Array.prototype.forEach.call(chipContainer.querySelectorAll('[data-state-chip]'), function (c) {
                c.classList.toggle('is-active', c === chip);
              });
            }
          }
        }
      } catch (err) {}
    }

    readUrlParams();
    applyFilter();
  }

  function boot() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-section-type="store-locator"]'),
      init
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector) {
      var section = event.target.querySelector('[data-section-type="store-locator"]');
      if (section) init(section);
    }
  });
})();
